import { useEffect, useState } from 'react';

/**
 * An identifier you can actually take somewhere else.
 *
 * Provider ids and hashes are things an operator retypes into Ads Manager, a
 * log-triage query or a bug report, and a long id retyped by hand is a typo
 * waiting to happen. Falls back silently when the page is not a secure
 * context, where `navigator.clipboard` is undefined — the id is still
 * selectable, so a missing button costs nothing.
 */
export function CopyableId({
  value,
  size = 'sm',
  truncate = false,
}: {
  value: string;
  size?: 'sm' | 'touch';
  /** One line with an ellipsis instead of wrapping (a header beside a title). */
  truncate?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(timer);
  }, [copied]);
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  return (
    <span
      className="copyable-id"
      style={{ display: 'inline-flex', gap: 8, alignItems: 'center', minWidth: 0 }}
    >
      <span
        className="mono"
        title={truncate ? value : undefined}
        style={
          truncate
            ? { fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }
            : { fontSize: 11, overflowWrap: 'anywhere' }
        }
      >
        {value}
      </span>
      {clipboard && (
        <button
          type="button"
          className={`btn btn-sm copyable-id-btn${size === 'touch' ? ' copyable-id-btn--touch' : ''}`}
          style={{ padding: '0 6px', fontSize: 10 }}
          title={`Copy ${value}`}
          aria-label={`Copy ${value}`}
          onClick={() => {
            void clipboard.writeText(value).then(
              () => setCopied(true),
              () => {},
            );
          }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
      )}
    </span>
  );
}
